import { useCallback, useEffect, useMemo } from 'react';
import { Form, Formik } from 'formik';
import { promoCodeSchema } from '@/service/schemas/Cart.schema';
import { useCartStore } from '@/service/state/Cart/useCartStore';
import { ECartActionType } from '@/service/state/Cart/domain';
import { FormInput } from '@/ui/common/Inputs/FormInput';
import { FormButton } from '@/ui/common/Buttons/FormButton';
import { WarningIcon } from '@/ui/common/Icons/WarningIcon';
import { Toast } from '@/ui/common/Toast/Toast';
import { ToastScreen } from './ToastScreen';
import { usePartnerStore } from '@/service/state/Partner/usePartnerStore';

type TPromoCodeProps = {
    promoCode: string;
    partnerSlug: string;
    promoDescription?: string;
};

export const PromoCode = ({ promoCode, promoDescription, partnerSlug }: TPromoCodeProps) => {
    const initialValues = useMemo(() => ({ promoCode }), [promoCode]);

    const { delCartPromo, addCartPromo, success, clearSuccess, error, clearError } = useCartStore();
    const promoText = usePartnerStore((state) => state.texts?.['Have a Promo Code?']);

    const showToast =
        (success.actionType === ECartActionType.ADD_PROMO_CODE_TO_CART && !!success.message) ||
        (error.actionType === ECartActionType.ADD_PROMO_CODE_TO_CART && !!error.message);

    useEffect(() => {
        if (!showToast) return;
        if (success.message) clearSuccess(ECartActionType.ADD_PROMO_CODE_TO_CART);
        if (error.message) clearError(ECartActionType.ADD_PROMO_CODE_TO_CART);
    }, [clearError, clearSuccess, error.message, showToast, success.message]);

    const handleSubmit = useCallback(
        (data: Omit<TPromoCodeProps, 'partnerSlug'>) => {
            if (promoCode) {
                delCartPromo({ partnerId: partnerSlug });
            } else {
                addCartPromo({ partnerId: partnerSlug }, { code: data.promoCode });
            }
        },
        [addCartPromo, delCartPromo, partnerSlug, promoCode]
    );

    return (
        <>
            <Toast
                show={showToast}
                screen={
                    <ToastScreen
                        title={error.message ? '' : `Promocode ${promoCode}`}
                        description={
                            error.message
                                ? error.message
                                : "Thanks for visiting. Today's discount has been applied to your shopping cart"
                        }
                    />
                }
            />
            <Formik
                initialValues={initialValues}
                validationSchema={promoCodeSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ isValid, touched, values }) => (
                    <Form>
                        <div
                            className={`w-full rounded-xl bg-ash px-5 py-4 dark:bg-blue-1 md:rounded-2xl md:px-10 md:py-8 lg:px-11 lg:py-10 ${
                                touched.promoCode && !isValid
                                    ? 'min-h-[160px] md:min-h-[212px]'
                                    : ' min-h-max'
                            }`}
                            data-test-id="cart-promo-form"
                        >
                            <div
                                className={`flex w-full justify-between gap-x-[11px] md:gap-x-4 ${
                                    promoCode
                                        ? 'flex-col items-start gap-y-[15px] md:flex-row md:items-end md:gap-y-0'
                                        : 'items-end'
                                }`}
                            >
                                {promoCode ? (
                                    <div className="flex flex-col self-start">
                                        <span
                                            className="tx-large-semibold text-gray-6 dark:text-white"
                                            data-test-id="promo-value"
                                        >
                                            Promotion Code{' '}
                                            <span className="text-brand">{promoCode}</span>
                                        </span>
                                        <span className="tx-large-regular text-gray-2 dark:text-white">
                                            {promoDescription
                                                ? promoDescription
                                                : 'has been applied'}
                                        </span>
                                    </div>
                                ) : (
                                    <FormInput
                                        className="promo-code-widget max-w-full gap-4 lg:gap-6"
                                        name="promoCode"
                                        label={promoText || 'Have a promo code?'}
                                        placeholder="Enter Promo Code"
                                        errorIcon={<WarningIcon />}
                                        data-test-id="promo-input"
                                    />
                                )}
                                <FormButton
                                    isSubmit
                                    variant="outlined"
                                    size="md"
                                    borderColor="custom"
                                    className={`m-0 h-9 px-0 md:h-[50px] md:max-w-max md:px-10 lg:h-[45px] ${
                                        promoCode
                                            ? 'max-w-full border-gray-3 text-gray-2 dark:border-dark-1 dark:text-white'
                                            : 'max-w-[96px] border-blue-2 hover:border-blue-7'
                                    }`}
                                    label={promoCode ? 'Remove promo code' : 'Apply code'}
                                    disabled={!isValid || !values.promoCode}
                                    data-test-id="promo-btn"
                                />
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>
        </>
    );
};
